/**
 * Role-Based Permissions System
 *
 * Single source of truth for all permission definitions, role mappings,
 * and utility functions. Permissions are defined in code (not database).
 * Roles are additive - a user with multiple roles gets the union of all permissions.
 */

import type { Role } from "./roles";

export const PERMISSIONS = {
  AUCTIONS_VIEW: "auctions.view",
  AUCTIONS_MANAGE: "auctions.manage",
  AUCTIONS_APPROVE_BIDDERS: "auctions.approve_bidders",
  // BL-2279 (PRJ-89): move a closed auction through its post-close settlement
  // stages. Admin holds it via Object.values; Accounting holds it too (BL-2283).
  AUCTIONS_CLOSE_STAGE: "auctions.close_stage",
  INVENTORY_VIEW: "inventory.view",
  INVENTORY_MANAGE: "inventory.manage",
  CUSTOMERS_VIEW: "customers.view",
  CUSTOMERS_MANAGE: "customers.manage",
  CUSTOMERS_READ_ONLY: "customers.read_only",
  CUSTOMERS_ACCOUNTING_TAB: "customers.accounting_tab",
  // BL-2420 (ADR 062 — Access Grants step 5): a customer's standing moves (ban,
  // suspend, reactivate) and delete/restore, apart from editing their details.
  // Admin only, via Object.values (Michael Lawrie, 2026-10-12).
  CUSTOMERS_SET_STANDING: "customers.set_standing",
  CUSTOMERS_ARCHIVE: "customers.archive",
  INVOICES_VIEW: "invoices.view",
  INVOICES_MANAGE: "invoices.manage",
  SETTLEMENTS_VIEW: "settlements.view",
  SETTLEMENTS_MANAGE: "settlements.manage",
  SETTLEMENTS_ADJUST: "settlements.adjust",
  PAYMENTS_VIEW: "payments.view",
  PAYMENTS_MANAGE: "payments.manage",
  PAYMENT_FEES_VIEW: "payment_fees.view",
  CLERKING_VIEW: "clerking.view",
  CLERKING_MANAGE: "clerking.manage",
  SETTINGS_VIEW: "settings.view",
  SETTINGS_USERS: "settings.users",
  SETTINGS_ACCOUNTING: "settings.accounting",
  SETTINGS_AUCTIONS: "settings.auctions",
  SETTINGS_WEBSITE: "settings.website",
  SETTINGS_SUPPORT: "settings.support",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

const ADMIN_PERMISSIONS: Permission[] = Object.values(PERMISSIONS);

const CATALOGER_PERMISSIONS: Permission[] = [
  PERMISSIONS.INVENTORY_VIEW,
  PERMISSIONS.INVENTORY_MANAGE,
];

// BL-2283 (PRJ-89 D4): Accounting runs settlement, so it sees auctions read-only
// and moves closed ones through their closing stages. No other auction power.
const ACCOUNTING_PERMISSIONS: Permission[] = [
  PERMISSIONS.AUCTIONS_VIEW,
  PERMISSIONS.AUCTIONS_CLOSE_STAGE,
  PERMISSIONS.CUSTOMERS_READ_ONLY,
  PERMISSIONS.CUSTOMERS_ACCOUNTING_TAB,
  PERMISSIONS.INVOICES_VIEW,
  PERMISSIONS.INVOICES_MANAGE,
  PERMISSIONS.SETTLEMENTS_VIEW,
  PERMISSIONS.SETTLEMENTS_MANAGE,
  PERMISSIONS.SETTLEMENTS_ADJUST,
  PERMISSIONS.PAYMENTS_VIEW,
  PERMISSIONS.PAYMENTS_MANAGE,
  PERMISSIONS.PAYMENT_FEES_VIEW,
  PERMISSIONS.SETTINGS_ACCOUNTING,
  PERMISSIONS.SETTINGS_SUPPORT,
];

// BL-1446: the Clerk role was retired (its 4-permission bundle was a strict
// subset of Admin's full set; holders were converted to Admin by migration).
// CLERKING_VIEW / CLERKING_MANAGE stay as permissions — Admin holds them, and
// ~20 routes use CLERKING_MANAGE as a narrower-than-AUCTIONS_MANAGE gate.
// BL-1468: the Auction Coordinator and Sales roles were retired (holders
// converted to Cataloger by migration — a deliberate access narrowing:
// auctions.* and customers.* now require Admin). Every permission both
// bundles carried survives as a permission; only the roles are gone.

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  "Super Admin": ADMIN_PERMISSIONS,
  Admin: ADMIN_PERMISSIONS,
  Cataloger: CATALOGER_PERMISSIONS,
  Accounting: ACCOUNTING_PERMISSIONS,
};

export function hasPermission(
  roles: Role[],
  permission: Permission,
  isSuperAdmin = false,
): boolean {
  if (isSuperAdmin) return true;
  if (!roles || roles.length === 0) return false;

  return roles.some((role) => {
    const perms = ROLE_PERMISSIONS[role];
    return perms && perms.includes(permission);
  });
}

export function hasAnyPermission(
  roles: Role[],
  permissions: readonly Permission[],
  isSuperAdmin = false,
): boolean {
  if (isSuperAdmin) return true;
  return permissions.some((p) => hasPermission(roles, p));
}

export function hasAllPermissions(
  roles: Role[],
  permissions: Permission[],
  isSuperAdmin = false,
): boolean {
  if (isSuperAdmin) return true;
  return permissions.every((p) => hasPermission(roles, p));
}

export function getPermissionsForRoles(roles: Role[]): Set<Permission> {
  const perms = new Set<Permission>();
  for (const role of roles) {
    const rolePerms = ROLE_PERMISSIONS[role];
    if (rolePerms) {
      for (const p of rolePerms) {
        perms.add(p);
      }
    }
  }
  return perms;
}

/**
 * Who may open the Auctions list (/auctions): an auctions viewer, or an inventory
 * viewer (a Cataloger). BL-2358 (Michael Lawrie, 2026-10-09: "cataloger should be
 * able to see everything on the auctions page as it is today"). The list page's
 * gate and the sidebar entry both read this one set. It widens only the LIST: a
 * Cataloger still holds no AUCTIONS_VIEW, so every auction.read / lot.read route
 * and every tab but Lot Info stay closed to them (BL-2349's Lots-only viewer).
 */
export const AUCTIONS_LIST_VIEW_PERMISSIONS: readonly Permission[] = [
  PERMISSIONS.AUCTIONS_VIEW,
  PERMISSIONS.INVENTORY_VIEW,
];

/**
 * Who may open the phone app's Offline page (/offline): every role that can queue
 * an offline write, since its Change queue tab is the only place a refused or
 * failed change is retried, discarded or overwritten (BUG-2072, ADR 066 — Offline
 * Mode § 6.1 limit 4). Every write the device queues is on a lot (an edit, a
 * create, a placement, a status change, a lot note), and a lot note is open to
 * every role that can open the lot, so the set is the permissions behind the
 * lot-open grant `LOT_EDIT_MODAL_READ` (`lot.read`, `inventory.read`,
 * `invoice.read`, `settlement.read`). The page gate and the sidebar entry both
 * read this one set. `offline-page-reach.test.ts` pins it, role by role, to the
 * registry grants of every route the offline transport queues.
 */
export const OFFLINE_PAGE_PERMISSIONS: readonly Permission[] = [
  PERMISSIONS.AUCTIONS_VIEW,
  PERMISSIONS.INVENTORY_VIEW,
  PERMISSIONS.INVOICES_VIEW,
  PERMISSIONS.SETTLEMENTS_VIEW,
];

/**
 * Who may open Settings home (/settings): any role holding one settings permission.
 * The sidebar entry and the home page's gate both read this one set, so the entry
 * locks exactly when the page shows No access (BUG-2187).
 */
export const SETTINGS_PAGE_PERMISSIONS: readonly Permission[] = [
  PERMISSIONS.SETTINGS_VIEW,
  PERMISSIONS.SETTINGS_USERS,
  PERMISSIONS.SETTINGS_ACCOUNTING,
  PERMISSIONS.SETTINGS_AUCTIONS,
  PERMISSIONS.SETTINGS_WEBSITE,
  PERMISSIONS.SETTINGS_SUPPORT,
];

export const NAV_PERMISSIONS: Record<string, Permission> = {
  "/auctions": PERMISSIONS.AUCTIONS_VIEW,
  "/inventory": PERMISSIONS.INVENTORY_VIEW,
  "/customers": PERMISSIONS.CUSTOMERS_VIEW,
  // BL-2519: the sidebar's Accounting section, gated on the permissions its
  // pages' grants map to (`invoice.read`, `settlement.read`: Admin and Accounting).
  "/invoices": PERMISSIONS.INVOICES_VIEW,
  "/settlements": PERMISSIONS.SETTLEMENTS_VIEW,
  // BL-2824: Transactions opens on `payment.read`, which maps to payments.view (Admin and Accounting).
  "/transactions": PERMISSIONS.PAYMENTS_VIEW,
  "/settings": PERMISSIONS.SETTINGS_VIEW,
  // BL-2342: the phone app's Offline page is gated, not an open default.
  // BUG-2072: `canSeeNavItem` reads the any-of set OFFLINE_PAGE_PERMISSIONS.
  "/offline": PERMISSIONS.INVENTORY_VIEW,
};

export function canSeeNavItem(
  href: string,
  roles: Role[],
  isSuperAdmin = false,
): boolean {
  if (isSuperAdmin) return true;

  // Super Admin-only routes: never visible to non-SA users, regardless of permissions.
  // Matches /super-admin and any sub-path or query-string form (e.g. /super-admin?tab=users).
  if (href === "/super-admin" || href.startsWith("/super-admin/") || href.startsWith("/super-admin?")) {
    return false;
  }

  const requiredPermission = NAV_PERMISSIONS[href];
  if (!requiredPermission) return true;

  if (href === "/auctions") {
    return hasAnyPermission(roles, AUCTIONS_LIST_VIEW_PERMISSIONS);
  }

  if (href === "/offline") {
    return hasAnyPermission(roles, OFFLINE_PAGE_PERMISSIONS);
  }

  if (href === "/customers") {
    return hasAnyPermission(roles, [
      PERMISSIONS.CUSTOMERS_VIEW,
      PERMISSIONS.CUSTOMERS_READ_ONLY,
    ]);
  }

  if (href === "/settings") {
    return hasAnyPermission(roles, SETTINGS_PAGE_PERMISSIONS);
  }

  return hasPermission(roles, requiredPermission);
}

export const SETTINGS_TAB_PERMISSIONS: Record<string, Permission> = {
  Users: PERMISSIONS.SETTINGS_USERS,
  Accounting: PERMISSIONS.SETTINGS_ACCOUNTING,
  Auctions: PERMISSIONS.SETTINGS_AUCTIONS,
  Website: PERMISSIONS.SETTINGS_WEBSITE,
  Support: PERMISSIONS.SETTINGS_SUPPORT,
  // Payments: removed — payment provider config moved to Super Admin → Payments tab.
  // Those routes (/api/tenants/[id]/payment-settings + /finix/onboarding) are
  // super-admin only, declared `{ superAdmin }` in the registry (BL-1833 PR 4b).
};

/**
 * Where a signed-in staff user lands. Someone who runs auctions lands on them;
 * a view-only auctions holder with customer access (Accounting, BL-2283 D9)
 * keeps landing on Customers, where their work starts.
 *
 * @param roles - The user's roles at the active tenant.
 * @param isSuperAdmin - The platform bypass.
 * @returns The landing path.
 */
export function getDefaultPage(roles: Role[], isSuperAdmin = false): string {
  if (isSuperAdmin || hasPermission(roles, PERMISSIONS.AUCTIONS_MANAGE)) return "/auctions";
  if (hasAnyPermission(roles, [PERMISSIONS.CUSTOMERS_VIEW, PERMISSIONS.CUSTOMERS_READ_ONLY])) return "/customers";
  if (hasPermission(roles, PERMISSIONS.AUCTIONS_VIEW)) return "/auctions";
  if (hasPermission(roles, PERMISSIONS.INVENTORY_VIEW)) return "/inventory";
  if (
    hasAnyPermission(roles, [
      PERMISSIONS.SETTINGS_VIEW,
      PERMISSIONS.SETTINGS_USERS,
      PERMISSIONS.SETTINGS_ACCOUNTING,
      PERMISSIONS.SETTINGS_AUCTIONS,
      PERMISSIONS.SETTINGS_WEBSITE,
      PERMISSIONS.SETTINGS_SUPPORT,
    ])
  ) return "/settings";
  return "/auth/unauthorized";
}

/**
 * One line per tenant-assignable role, shown above the roles panel's list and
 * on the invite modal's role cards. Each must agree with what that role's panel
 * tab lists (`capabilitiesForRole` in `./role-grants`, BL-1883).
 */
export const ROLE_DESCRIPTIONS: Record<Exclude<Role, "Super Admin">, string> = {
  Admin: "Full access to all features including user management, settings, and configuration.",
  Cataloger: "Add and edit items in Inventory, including their photos and files, see the Auctions list, and add new lots from an auction's Lots tab.",
  Accounting: "View auctions and move closed auctions through their closing stages. See their lots and edit a lot's tax, load-out fee and title paperwork. Correct sales after an auction, and manage accounting settings, support settings and company details. Look up customers, see their accounting and manage their documents.",
};
